import { useCallback, useState } from "react";
import { Column, ColumnAlignType } from "primereact/column";
import moment from "moment";

import { REQUEST_STATUS } from "../constants/status";
import {
  TABLE_COLUMN_KEYS,
  VACATION_REQUESTS_COLUMNS,
} from "../constants/columns";
import { VACATION_REQUESTS_STRINGS } from "../constants/strings";

import { bodyClassName, headerClassName } from "assets/customStyles";

import { GeneralNames, TableNames } from "namesConstants/names";

import { ColaboraTable, SidePortal } from "components/v2";
import { FullLoading } from "components/FullLoading";
import { Toast } from "components/toast";

import { useCurrentCompany } from "hooks/useCurrentCompany";
import { useFilteredTableData } from "hooks/useFilteredTableData";
import { useQueryError } from "hooks/useQueryError";
import { useTableBehavior } from "hooks/useTableBehavior";

import useReload from "hooks/useReload";

import { useExportToExcel } from "../hooks/useExportToExcel";
import { useVacationRequests } from "../hooks";
import { usePeriodTypes } from "shared/hooks";

import { canUpdateStatus } from "../utils/requestStatus";

import { TableForm } from "./TableForm";
import StatusChip from "./StatusChip";
import TableActions from "./TableActions";
import RequestDetail from "./RequestDetail";
import RejectRequest from "./RejectRequest";
import ApplyConfirmDialog from "./ApplyConfirmDialog";
import ApplyResultDialog from "./ApplyResultDialog";
import type { RequestStatusT } from "../domain/types/requestStatus.types";

export const RequestsTable = () => {
  const [currentRequest, setCurrentRequest] = useState(null);
  const [rejectRequestIds, setRejectRequestIds] = useState([]);
  const [pendingApply, setPendingApply] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const { exportToExcel, isExporting } = useExportToExcel();

  const companyInfo = useCurrentCompany();

  const {
    isLoading: isPeriodTypesLoading,
    isError: isPeriodTypesError,
    error: periodTypesError,
  } = usePeriodTypes();

  const {
    searchTextFilter,
    selectedRows,
    sort,
    tableFilters,
    clearSelectedRows,
    onSelectionChange,
    resetSearchTextFilter,
    setSearchTextFilter,
    setSort,
    updateTableFilters,
  } = useTableBehavior({
    initialSort: [{ field: TABLE_COLUMN_KEYS.START_DATE, order: 1 }],
    initialFilters: {
      status: [REQUEST_STATUS.PENDING, REQUEST_STATUS.APPROVED],
      startDate: null,
      endDate: null,
      periodTypeId: null,
      periodStartDate: null,
      periodEndDate: null,
    },
  });

  const hasAnyCalendarDate = !!(tableFilters.startDate || tableFilters.endDate);

  const resolvedStartDate = hasAnyCalendarDate
    ? tableFilters.startDate
    : tableFilters.periodStartDate;

  const resolvedEndDate = hasAnyCalendarDate
    ? tableFilters.endDate
    : tableFilters.periodEndDate;

  const {
    data,
    isLoading,
    isError: isRequestsError,
    error: requestsError,
    refetch,
    isRefetching,
    updateVacationRequest: {
      isPending: isUpdating,
      mutateAsync: updateRequest,
    },
    applyRequests: { isPending: isApplying, mutateAsync: applyRequests },
  } = useVacationRequests({
    colaboraInstanceId: companyInfo?.instanceId,
    enabled: !isPeriodTypesLoading,
    startDate: moment(resolvedStartDate).isValid()
      ? moment(resolvedStartDate).format("YYYY-MM-DD")
      : null,
    endDate: moment(resolvedEndDate).isValid()
      ? moment(resolvedEndDate).format("YYYY-MM-DD")
      : null,
    periodTypeId: tableFilters.periodTypeId,
  });

  useReload([refetch]);
  useQueryError({
    isError: isRequestsError,
    error: requestsError,
    message: VACATION_REQUESTS_STRINGS.GET_DATA_ERROR_TITLE,
  });
  useQueryError({
    isError: isPeriodTypesError,
    error: periodTypesError,
    message: VACATION_REQUESTS_STRINGS.GET_DATA_PERIOD_ERROR_TITLE,
  });

  const filterPredicate = useCallback((request, filters) => {
    const statusFilter = filters.status;

    if (Array.isArray(statusFilter) && statusFilter.length === 0) return false;
    if (Array.isArray(statusFilter) && !statusFilter.includes(request.status))
      return false;

    return true;
  }, []);

  const getSearchableFields = useCallback(
    (request) => [
      request.code,
      request.fullName,
      request.requestType,
      request.processedBy,
    ],
    [],
  );

  const {
    filteredData: memoizedRequests,
    fullyFilteredData: fullyFilteredRequests,
  } = useFilteredTableData({
    data,
    tableFilters,
    searchTextFilter,
    filterPredicate,
    getSearchableFields,
  });

  const handleRequestAction = async (
    requestId: string,
    status: RequestStatusT,
  ) => {
    // Aplicar pasa por POST /v1/incidence-requests (que envia al sistema base y, si OK, marca APPLIED en Colabora).
    // Las demas transiciones (ej. PENDING -> APPROVED) siguen siendo solo PATCH /v1/requests/status.
    if (status === REQUEST_STATUS.APPLIED) {
      try {
        const { results } = await applyRequests({ requestIds: [requestId] });
        const result = results?.[0];
        if (result?.isSuccess) {
          Toast("success", VACATION_REQUESTS_STRINGS.APPLY_SUCCESS_ALL);
        } else {
          Toast(
            "error",
            VACATION_REQUESTS_STRINGS.APPLY_ERROR_TITLE,
            result?.status,
          );
        }
        refetch();
      } catch (error) {
        Toast(
          "error",
          VACATION_REQUESTS_STRINGS.APPLY_ERROR_TITLE,
          error.message,
        );
      }
      return;
    }

    try {
      const { succeeded, failed } = await updateRequest({
        requestsToUpdate: [
          {
            requestId: requestId,
            newStatus: status,
          },
        ],
      });

      if (succeeded.length > 0) {
        Toast("success", VACATION_REQUESTS_STRINGS.UPDATE_DATA_SUCCESS_TITLE);
      }
      if (failed.length > 0) {
        Toast(
          "warning",
          VACATION_REQUESTS_STRINGS.UPDATE_PARTIAL_FAILURE_TITLE,
        );
      }
    } catch (error) {
      Toast(
        "error",
        VACATION_REQUESTS_STRINGS.UPDATE_DATA_ERROR_TITLE,
        error.message,
      );
    }
  };

  const actionsBodyTemplate = (rowData) => (
    <TableActions
      rowData={rowData}
      isUpdating={isUpdating}
      onUpdateRequest={handleRequestAction}
      onRejectRequest={handleIndividualReject}
      onViewRequest={setCurrentRequest}
    />
  );

  const handleExportToExcel = async () => {
    if (isExporting) return;

    if (fullyFilteredRequests.length === 0) {
      Toast("info", VACATION_REQUESTS_STRINGS.EXPORT_NO_DATA);
      return;
    }

    try {
      await exportToExcel({
        data: fullyFilteredRequests,
        sortConfig: sort,
      });

      Toast("success", VACATION_REQUESTS_STRINGS.EXPORT_SUCCESS);
    } catch (error) {
      Toast(
        "error",
        VACATION_REQUESTS_STRINGS.EXPORT_ERROR,
        error.message || "No se pudo generar el archivo",
      );
    }
  };

  const validateDataSelectable = (rowData) =>
    canUpdateStatus(rowData.data.status);

  const handleOnDoubleClickRow = (e) => setCurrentRequest(e.data);

  const handleOnClosePortal = () => {
    setCurrentRequest(null);
  };

  const handleIndividualReject = (requestId) => {
    setRejectRequestIds([requestId]);
  };

  const handleBulkReject = () => {
    const ids = selectedRows
      .filter(
        (row) =>
          row.status === REQUEST_STATUS.PENDING ||
          row.status === REQUEST_STATUS.APPROVED,
      )
      .map((row) => row.id);

    if (ids.length === 0) {
      Toast(
        "warning",
        VACATION_REQUESTS_STRINGS.EMPTY_REQUEST_TO_WARNING_TITLE,
      );
      return;
    }

    setRejectRequestIds(ids);
  };

  const handleRejectClose = () => {
    setRejectRequestIds([]);
    clearSelectedRows();
  };

  const handleBulkAction = async (status) => {
    const requestIds = selectedRows
      .filter((row) => row.status === REQUEST_STATUS.PENDING)
      .map((row) => row.id);

    try {
      if (requestIds.length === 0) {
        const message =
          status === REQUEST_STATUS.APPROVED
            ? VACATION_REQUESTS_STRINGS.EMPTY_REQUEST_TO_APPROVE_WARNING_TITLE
            : VACATION_REQUESTS_STRINGS.EMPTY_REQUEST_TO_WARNING_TITLE;

        Toast("warning", message);
        return;
      }

      const { succeeded, failed } = await updateRequest({
        requestsToUpdate: requestIds.map((requestId) => ({
          requestId,
          newStatus: status,
        })),
      });

      if (succeeded.length > 0) {
        Toast("success", VACATION_REQUESTS_STRINGS.UPDATE_DATA_SUCCESS_TITLE);
      }

      if (failed.length > 0) {
        Toast(
          "warning",
          VACATION_REQUESTS_STRINGS.UPDATE_PARTIAL_FAILURE_TITLE,
        );
      }

      clearSelectedRows();
    } catch (error) {
      Toast(
        "error",
        VACATION_REQUESTS_STRINGS.UPDATE_DATA_ERROR_TITLE,
        error.message,
      );
    }
  };
  const handleBulkApprove = () => handleBulkAction(REQUEST_STATUS.APPROVED);

  const handleBulkApply = () => {
    const eligible = selectedRows.filter(
      (row) => row.status === REQUEST_STATUS.APPROVED,
    );

    if (eligible.length === 0) {
      Toast(
        "warning",
        VACATION_REQUESTS_STRINGS.EMPTY_REQUEST_TO_APPLY_WARNING,
      );
      return;
    }

    setPendingApply({ rows: eligible });
  };

  const handleCancelApply = () => setPendingApply(null);

  const handleConfirmApply = async () => {
    if (!pendingApply) return;
    const rowsToApply = pendingApply.rows;
    setPendingApply(null);

    try {
      const { results } = await applyRequests({
        requestIds: rowsToApply.map((row) => row.id),
      });
      setApplyResult({ rows: rowsToApply, results: results ?? [] });
      clearSelectedRows();
    } catch (e) {
      Toast("error", VACATION_REQUESTS_STRINGS.APPLY_ERROR_TITLE, e.message);
    }
  };

  const handleCloseApplyResult = () => {
    if (!applyResult) return;
    const { results } = applyResult;
    const okCount = results.filter((r) => r.isSuccess).length;
    const errCount = results.length - okCount;

    if (errCount === 0) {
      Toast("success", VACATION_REQUESTS_STRINGS.APPLY_SUCCESS_ALL);
    } else if (okCount === 0) {
      Toast("error", VACATION_REQUESTS_STRINGS.APPLY_ERROR_TITLE);
    } else {
      Toast("warning", VACATION_REQUESTS_STRINGS.APPLY_PARTIAL);
    }

    setApplyResult(null);
  };

  const isFetching = isLoading || isRefetching;

  return (
    <section
      className="flex flex-col w-full h-full bg-white border rounded-md shadow-xl p-2 relative"
      aria-label="Listado de solicitudes de vacaciones"
      aria-busy={isFetching || isExporting || isUpdating || isApplying}
    >
      <div aria-live="assertive" aria-atomic="true">
        {(isExporting || isUpdating || isApplying) && (
          <FullLoading loadingState />
        )}
      </div>
      <div className="w-full h-full flex flex-col gap-2">
        <TableForm
          setSearchTextFilter={setSearchTextFilter}
          resetSearchTextFilter={resetSearchTextFilter}
          tableFilters={tableFilters}
          updateTableFilters={updateTableFilters}
          onClearSelectedRows={clearSelectedRows}
          onExportToExcel={handleExportToExcel}
          onBulkApprove={handleBulkApprove}
          onBulkReject={handleBulkReject}
          onBulkApply={handleBulkApply}
        />
        <ColaboraTable
          data={memoizedRequests}
          isLoadingData={isPeriodTypesLoading || isFetching}
          sort={sort}
          onChangeSort={setSort}
          selection={selectedRows}
          onSelectionChange={onSelectionChange}
          filters={searchTextFilter}
          emptyMessage={GeneralNames.GeneralEmptyMessage}
          currentPageReportTemplate={
            VACATION_REQUESTS_STRINGS.TEXT_TABLE_RESULTS
          }
          isDataSelectable={validateDataSelectable}
          globalFilterFields={[
            TABLE_COLUMN_KEYS.CODE,
            TABLE_COLUMN_KEYS.FULL_NAME,
            TABLE_COLUMN_KEYS.REQUEST_TYPE,
            TABLE_COLUMN_KEYS.PROCESSED_BY,
          ]}
          onRowDoubleClick={handleOnDoubleClickRow}
          rowClassName={() => "!cursor-pointer"}
        >
          <Column
            headerClassName={headerClassName}
            bodyClassName="!text-center !text-secondary-PCS004 !border-r-2 !border-b-0 !border-secondary-PCS004"
            selectionMode="multiple"
            style={{ minWidth: "2.5rem", width: "2.5rem" }}
            alignFrozen="left"
            frozen
          />
          {VACATION_REQUESTS_COLUMNS.map((col) => (
            <Column
              key={`${col.field}_vacation_requests`}
              headerClassName={headerClassName}
              style={col.style}
              field={col.field}
              header={col.header}
              body={col.field === "status" ? StatusChip : col.body}
              bodyClassName={bodyClassName}
              align={col.align as ColumnAlignType}
              sortable={col.sortable}
              alignFrozen="left"
              frozen={col.frozen}
            />
          ))}
          <Column
            headerClassName={headerClassName}
            style={{ minWidth: "8rem", width: "8rem" }}
            header={TableNames.TableActions}
            body={actionsBodyTemplate}
            bodyClassName={bodyClassName}
            align="center"
            alignFrozen="right"
            frozen
          />
        </ColaboraTable>
      </div>
      <SidePortal showContent={!!currentRequest} onClose={handleOnClosePortal}>
        {!!currentRequest && (
          <RequestDetail
            requestId={currentRequest?.id}
            onClose={handleOnClosePortal}
          />
        )}
      </SidePortal>
      <SidePortal
        showContent={rejectRequestIds.length > 0}
        onClose={() => setRejectRequestIds([])}
      >
        {rejectRequestIds.length > 0 && (
          <RejectRequest
            requestIds={rejectRequestIds}
            onClose={handleRejectClose}
          />
        )}
      </SidePortal>
      <ApplyConfirmDialog
        open={pendingApply !== null}
        count={pendingApply?.rows.length ?? 0}
        onCancel={handleCancelApply}
        onConfirm={handleConfirmApply}
      />
      <ApplyResultDialog
        visible={applyResult !== null}
        rows={applyResult?.rows ?? []}
        results={applyResult?.results ?? []}
        onClose={handleCloseApplyResult}
      />
    </section>
  );
};
